import GTP from "../../mappings/gtp.txt";
import GGD_P5 from "../../mappings/ggd_p5.txt";
import GGD_MM2 from "../../mappings/ggd_mm2.txt";
import {
  GGD_MM2_ALIASES,
  GGD_P5_ALIASES,
  GTP_ALIASES,
  type Alias,
} from "./aliases";
import type { PreferredNoteMapping } from "./preferred-note-mappings";

class NameToNote {
  private map: Map<string, Set<number>> = new Map();

  constructor(
    private readonly preferredNoteMapping: PreferredNoteMapping = {},
  ) {}

  add(name: string, note: number): void {
    const notes = this.map.get(name) ?? new Set();
    notes.add(note);
    this.map.set(name, notes);
  }

  get(name: string): Set<number> | null {
    return this.map.get(name) ?? null;
  }

  getOne(name: string): number | null {
    const notes = this.get(name);
    if (!notes) {
      return null;
    }

    const preferred = this.preferredNoteMapping[name];
    if (preferred && notes.has(preferred)) {
      return preferred;
    }

    return notes.values().next().value ?? null;
  }
}

export class ReaperDrumMap {
  noteToName: Map<number, string> = new Map();
  nameToNote: NameToNote;
  readonly entries: [number, string][];
  readonly invalidEntries: string[] = [];

  constructor(
    lines: [number, string][],
    aliases?: Alias[],
    preferredNoteMappings?: PreferredNoteMapping,
  ) {
    this.entries = lines;
    this.nameToNote = new NameToNote(preferredNoteMappings);

    for (const [note, name] of lines) {
      if (
        !Number.isInteger(note) ||
        note < 0 ||
        note > 127 ||
        name.endsWith("[invalid mapping]")
      ) {
        this.invalidEntries.push(`${note}: ${name}`);
        continue;
      }
      this.noteToName.set(note, name);
      this.nameToNote.add(name, note);

      const matchingAliases =
        aliases?.filter((alias) => alias.altName === name) ?? [];
      for (const alias of matchingAliases) {
        this.nameToNote.add(alias.name, note);
      }
    }
  }

  static fromName(name: DrumMap): ReaperDrumMap {
    switch (name) {
      case "GTP":
        return DrumMap.GTP;
      case "GGD_P5":
        return DrumMap.GGD_P5;
      case "GGD_MM2":
        return DrumMap.GGD_MM2;
    }
  }

  static parse(data: string, aliases?: Alias[]): ReaperDrumMap {
    const lines = data.split("\n");
    const input = lines.reduce<[number, string][]>((acc, line) => {
      const trimmed = line.trim();

      if (trimmed.startsWith("#")) {
        return acc;
      }

      if (trimmed === "") {
        return acc;
      }

      const [note, ...name] = trimmed.split(" ");
      if (!note) {
        return acc;
      }

      const noteValue = Number(note);
      const nameValue = name.join(" ");
      if (!/^\d+$/.test(note) || noteValue > 127 || nameValue.trim() === "") {
        // Invalid source lines are retained for validation but excluded from lookup.
        acc.push([Number.NaN, `${trimmed} [invalid mapping]`]);
        return acc;
      }
      return [...acc, [noteValue, nameValue]];
    }, []);

    return new ReaperDrumMap(input, aliases);
  }

  getName(note: number): string | null {
    return this.noteToName.get(note) ?? null;
  }

  getNote(name: string): number | null {
    return this.nameToNote.getOne(name);
  }
}

const DRUM_MAPS = ["GTP", "GGD_P5", "GGD_MM2"] as const;
export type DrumMap = (typeof DRUM_MAPS)[number];

export const DrumMap = {
  GTP: ReaperDrumMap.parse(GTP, GTP_ALIASES),
  GGD_P5: ReaperDrumMap.parse(GGD_P5, GGD_P5_ALIASES),
  GGD_MM2: ReaperDrumMap.parse(GGD_MM2, GGD_MM2_ALIASES),
  list: () => DRUM_MAPS,
};
